import { AddMember, MemberListType } from '@types';
import { formatISODate } from '@utils/date';

import { PaymentCollectionType } from '../../../constants';
import {
  getFileImportPaymentSchedule,
  getPaymentSchedule
} from '../../../helper';

export const getFeeCategoryData = (
  feeCategoryData: {
    id: number;
    label: string;
    value: string;
  }[]
) =>
  feeCategoryData.map((item: any) => ({
    branch_fee_category_id: item.id,
    amount: item.value
  }));

const getMonthlyConfigForAttendanceBasedFee = (
  item: MemberListType,
  groupDetails: any
) => {
  let dayOption = 'custom';
  let { dueDate } = item;
  const { startDate } = item;

  if (!dueDate && !startDate) {
    return groupDetails.payment_schedule.monthly_config;
  }

  if (!dueDate && startDate) {
    dueDate = new Date(startDate).getDate().toString();
  }

  if (dueDate === 'first_day' || dueDate === '1') {
    dayOption = 'first_day';
  } else if (dueDate === 'last_day') {
    dayOption = 'last_day';
  } else if (Number(dueDate) > 28) {
    dayOption = 'last_day';
  }

  if (dayOption === 'custom') {
    return {
      day_option: dayOption,
      custom_day: Number(dueDate)
    };
  }

  return {
    day_option: dayOption
  };
};

export const transformMembersArray = (
  memberList: MemberListType[],
  groupDetails: any,
  currentGroupDetails: any,
  isImmediatePayment: any,
  isCustomTerm: boolean,
  isFileUpload: boolean,
  isAttendanceBasedFee = false,
  isSessionBased = false,
  groupSessionsPerCycle = 0
) => {
  const getAttendancePaymentSchedule = (item: MemberListType) => ({
    interval: isSessionBased
      ? PaymentCollectionType.SESSION_BASED
      : PaymentCollectionType.MONTHLY,
    interval_frequency: 1,
    ...(isSessionBased
      ? {
          sessions_per_cycle:
            item.sessionsPerCycle || groupSessionsPerCycle || 0
        }
      : {
          monthly_config: getMonthlyConfigForAttendanceBasedFee(
            item,
            currentGroupDetails
          )
        }),
    ...(item.startDate && {
      start_date: formatISODate({
        date: item.startDate,
        separator: '-',
        format: 'yyyy-mm-dd'
      })
    })
  });

  const transformedMembersArray: AddMember[] = memberList.map((item) => ({
    name: `${item.name}`.trim(),
    mobile: item.primaryNumber.toString().replace(/\s+/g, ''),
    alternate_mobile: item?.altNumber?.toString().replace(/\s+/g, ''),
    country_code: item?.primaryNumberCountryCode,
    secondary_country_code: item?.altNumberCountryCode,
    email: item.email,
    amount:
      item?.amount && item?.amount !== '0'
        ? item.amount
        : groupDetails.groupAmount || currentGroupDetails?.amount,
    amount_paid_till_date: item?.amountPaid || 0,
    ...((groupDetails?.isFeeCategoryEnabled ||
      currentGroupDetails?.is_fee_configured) &&
      item.feeCategoryData && {
        member_fee_configuration: getFeeCategoryData(item.feeCategoryData)
      }),
    total_amount: item?.totalFee || 0,
    admission_number: item?.admissionNumber || null,
    admission_date: item?.admissionDate || null,
    date_of_birth: item?.dateOfBirth || null,
    guardian_name: item?.guardianName || null,
    age: item?.age || null,
    address_line_1: item?.addressLine1 || null,
    address_line_2: item?.addressLine2 || null,
    city: item?.city || null,
    state: item?.state || null,
    country: item?.country || null,
    pincode: item?.pincode || null,
    custom_fields: [
      ...('bloodGroup' in item
        ? [{ key: 'blood_group', value: item.bloodGroup }]
        : []),
      ...('isInsured' in item
        ? [
            {
              key: 'is_insured',
              value: item.isInsured === 'Yes' ? 'true' : 'false'
            }
          ]
        : []),
      ...('counsellorName' in item
        ? [{ key: 'counsellor_name', value: item.counsellorName }]
        : [])
    ],
    ...(item?.paymentCollectionType === PaymentCollectionType.ONCE &&
      !isImmediatePayment &&
      !isCustomTerm && {
        activation_date: formatISODate({
          date: item?.dueDate,
          separator: '-',
          format: 'yyyy-mm-dd'
        })
      }),
    ...(isAttendanceBasedFee && {
      payment_schedule: getAttendancePaymentSchedule(item)
    }),
    ...(!isAttendanceBasedFee &&
      item?.paymentCollectionType !== PaymentCollectionType.ONCE && {
        payment_schedule: isFileUpload
          ? getFileImportPaymentSchedule(
              item,
              isCustomTerm,
              currentGroupDetails?.payment_schedule?.term_details ||
                groupDetails.terms,
              currentGroupDetails?.is_fee_configured ||
                groupDetails.isFeeCategoryEnabled,
              currentGroupDetails?.payment_schedule?.start_date,
              currentGroupDetails?.payment_schedule?.interval,
              currentGroupDetails?.payment_schedule?.interval_frequency
            )
          : getPaymentSchedule({
              memberItem: item,
              isCustomTerm,
              isFeeCategoryEnabled:
                currentGroupDetails?.is_fee_configured ||
                groupDetails.isFeeCategoryEnabled,
              termsList:
                currentGroupDetails?.payment_schedule?.term_details ||
                groupDetails.terms
            })
      })
  }));
  return { transformedMembersArray };
};
